import * as THREE from "three";
import { Cell } from "@highmarch/domain";
import { BakedRange, hideBaked } from "./staticBatch";
import { Contact, GroundContacts } from "../groundSlope";
import { Spot, isLow, lowShipped, scaleOf } from "./foliageKit";
import { cellHash, lib } from "./common";

/**
 * Which of the placed props are hidden (foliage.ts FoliageView owns one): dressing the road now covers, and the portal
 * clearance — props taller than a waist-high rock near the portal and the first road cells, so a boss rising there is
 * never behind a tree (foliageKit.ts PORTAL_CLEAR). A hidden map blocker gets a low stand-in rock; a hidden tall prop
 * takes its contact shadow with it. Props are only ever hidden in place, never re-placed.
 */
export class PropClearance {
  /** Every placed prop (map and scenery). */
  private readonly spots: Spot[] = [];
  /** Cells currently kept clear round the portal. */
  zone = new Set<string>();
  /** Dressing hidden because the road covers its cell. */
  underRoad = new Set<THREE.Object3D>();

  /** `baked`: where each baked prop's vertices landed; `group`: where a stand-in goes; `rocks`: the biome's rock pool. */
  constructor(private readonly baked: ReadonlyMap<THREE.Object3D, BakedRange[]>, private readonly group: THREE.Group, private readonly rocks: () => readonly string[]) {}

  /** Hides or shows each prop for the road (dressing under it) and the portal clearance (tall props near it). */
  apply(): void {
    let contactsMoved = false;
    for (const sp of this.spots) {
      const cleared = sp.tall && this.zone.has(`${sp.x}|${sp.z}`);
      const hidden = cleared || this.underRoad.has(sp.prop);
      const ranges = this.baked.get(sp.prop);
      if (ranges) hideBaked(ranges, hidden);
      else sp.prop.visible = !hidden;
      if (cleared !== sp.cleared) {
        sp.cleared = cleared;
        if (sp.blocker && !sp.far) {
          sp.stand ??= cleared ? this.standIn(sp) : null;
          if (sp.stand) sp.stand.visible = cleared;
        } else if (sp.contact) {
          // a tall prop's contact shadow would stay behind as a dark stain where it stood
          const i = GroundContacts.list.indexOf(sp.contact);
          if (cleared && i >= 0) GroundContacts.list.splice(i, 1);
          else if (!cleared && i < 0) GroundContacts.list.push(sp.contact);
          contactsMoved = true;
        }
      }
    }
    if (contactsMoved) GroundContacts.version++;
  }

  /** Records a placed prop for the portal clearance. */
  track(prop: THREE.Object3D, c: Cell, name: string, blocker: boolean, far: boolean, contact: Contact | null): void {
    const sp: Spot = { prop, x: c.x, z: c.y, tall: !isLow(name), blocker, far, cleared: false, stand: null, contact };
    this.spots.push(sp);
    this.spotByProp.set(prop, sp);
  }
  private readonly spotByProp = new Map<THREE.Object3D, Spot>();
  spotOf(prop: THREE.Object3D | undefined): Spot | undefined { return prop && this.spotByProp.get(prop); }

  /** A low rock in place of a tall blocker the portal clearance hides: live, not baked (only a few ever show). */
  private standIn(sp: Spot): THREE.Object3D | null {
    const L = lib();
    if (!L) return null;
    const low = lowShipped(this.rocks(), L);
    if (low.length === 0) return null;
    const name = low[cellHash(new Cell(sp.x, sp.z)) % low.length];
    const o = L.clone(`hq:${name}`);
    o.scale.multiplyScalar(scaleOf(name, o));
    o.position.copy(sp.prop.position);
    o.rotation.y = sp.prop.rotation.y;
    this.group.add(o);
    return o;
  }
}
